<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import TeamsAPI from 'dashboard/api/teams';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { getWorkflowStageLabel } from 'dashboard/helper/workflowStageHelper';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const teams = useMapGetter('teams/getTeams');

const isOpen = ref(false);
const isUpdating = ref(false);
const isPickingDeveloper = ref(false);
const isFetchingDevelopers = ref(false);
const developers = ref([]);
const selectedDeveloperId = ref('');
const pendingAction = ref(null);

const ACTIONS_FOR_STAGE = {
  open: [
    { target: 'assigned_qa', label: 'Assign to QA (investigate)' },
    {
      target: 'assigned_dev',
      label: 'New Task',
      workflowType: 'task',
      requiresDeveloper: true,
    },
    {
      target: 'assigned_dev',
      label: 'Change Request',
      workflowType: 'change_request',
      requiresDeveloper: true,
    },
  ],
  assigned_qa: [
    { target: 'qa_confirmed_bug', label: 'Confirm Bug' },
    { target: 'qa_rejected', label: 'Not a Bug' },
  ],
  qa_confirmed_bug: [
    { target: 'assigned_dev', label: 'Assign to Development', requiresDeveloper: true },
  ],
  qa_rejected: [{ target: 'resolved', label: 'Close (Not a Bug)' }],
  assigned_dev: [{ target: 'in_progress', label: 'Start Progress' }],
  in_progress: [{ target: 'to_be_tested', label: 'Mark as To Be Tested' }],
  to_be_tested: [
    { target: 'resolved', label: 'Resolved' },
    { target: 'in_progress', label: 'Still Broken' },
  ],
  resolved: [],
};

const currentStage = computed(() => props.conversation.workflow_stage || 'open');
const currentType = computed(() => props.conversation.workflow_type || 'bug');

const stageLabel = computed(() =>
  getWorkflowStageLabel(currentStage.value, currentType.value)
);

const menuActions = computed(() => ACTIONS_FOR_STAGE[currentStage.value] || []);
const menuItems = computed(() =>
  menuActions.value.map((action, index) => ({
    label: action.label,
    action: 'workflow',
    value: index,
  }))
);
const hasActions = computed(() => menuActions.value.length > 0);

const closeAll = () => {
  isOpen.value = false;
  isPickingDeveloper.value = false;
  selectedDeveloperId.value = '';
  pendingAction.value = null;
};

const performTransition = async (targetStage, assigneeId = null, workflowType = null) => {
  isUpdating.value = true;
  try {
    await store.dispatch('updateWorkflowStage', {
      conversationId: props.conversation.id,
      workflowStage: targetStage,
      assigneeId,
      workflowType,
    });
    closeAll();
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      'Unable to update stage — you may not have permission for this step.';
    useAlert(message);
  } finally {
    isUpdating.value = false;
  }
};

const openDeveloperPicker = async action => {
  pendingAction.value = action;
  isPickingDeveloper.value = true;
  const devTeam = teams.value.find(
    team => (team.name || '').toLowerCase() === 'development'
  );
  if (!devTeam) {
    useAlert('No "Development" team found for this account.');
    return;
  }
  isFetchingDevelopers.value = true;
  try {
    const response = await TeamsAPI.getAgents({ teamId: devTeam.id });
    developers.value = response.data || [];
  } catch (error) {
    useAlert('Unable to load Development team members.');
  } finally {
    isFetchingDevelopers.value = false;
  }
};

const confirmDeveloperAssignment = () => {
  if (!selectedDeveloperId.value || !pendingAction.value) return;
  performTransition(
    pendingAction.value.target,
    Number(selectedDeveloperId.value),
    pendingAction.value.workflowType
  );
};

const handleMenuAction = ({ value }) => {
  const action = menuActions.value[value];
  if (!action) return;
  if (action.requiresDeveloper) {
    openDeveloperPicker(action);
    return;
  }
  performTransition(action.target);
};
</script>

<template>
  <div v-on-click-outside="closeAll" class="relative">
    <button
      type="button"
      class="flex items-center gap-1 h-8 px-2.5 rounded-md bg-n-alpha-2 text-xs font-medium text-n-slate-12 max-w-[9.5rem] sm:max-w-[14rem] disabled:cursor-default"
      :disabled="!hasActions"
      @click="isOpen = !isOpen"
    >
      <span class="truncate">{{ stageLabel }}</span>
      <span
        v-if="hasActions"
        class="i-lucide-chevron-down size-3 flex-shrink-0"
      />
    </button>

    <div
      v-if="isOpen"
      class="absolute z-50 mt-1 top-full w-64 max-w-[calc(100vw-2rem)] left-0 xl:left-auto xl:ltr:right-0 xl:rtl:left-0"
    >
      <template v-if="isPickingDeveloper">
        <div
          class="bg-n-alpha-3 backdrop-blur-[100px] border-0 outline outline-1 outline-n-container rounded-xl shadow-lg p-3 flex flex-col gap-2"
        >
          <p class="text-xs font-medium text-n-slate-11 mb-0">
            Select a developer
          </p>
          <select
            v-model="selectedDeveloperId"
            class="reset-base w-full h-9 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 px-2"
            :disabled="isFetchingDevelopers"
          >
            <option value="" disabled>
              {{ isFetchingDevelopers ? 'Loading…' : 'Choose developer' }}
            </option>
            <option v-for="dev in developers" :key="dev.id" :value="dev.id">
              {{ dev.name }}
            </option>
          </select>
          <div class="flex items-center justify-end gap-2 mt-1">
            <button
              type="button"
              class="text-xs text-n-slate-11 px-2 py-1.5"
              :disabled="isUpdating"
              @click="closeAll"
            >
              Cancel
            </button>
            <button
              type="button"
              class="text-xs font-medium text-white bg-n-blue-9 rounded-lg px-3 py-1.5 disabled:opacity-50"
              :disabled="isUpdating || !selectedDeveloperId"
              @click="confirmDeveloperAssignment"
            >
              {{ isUpdating ? 'Saving…' : 'Confirm' }}
            </button>
          </div>
        </div>
      </template>
      <DropdownMenu
        v-else
        :menu-items="menuItems"
        :is-loading="isUpdating"
        class="!static !w-full"
        @action="handleMenuAction"
      />
    </div>
  </div>
</template>
